import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pin, Lock, Info, AlertCircle } from "lucide-react";
import { AdvancedRangeSlider } from "./advanced-range-slider";
import { cn } from "@/utils/cn";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PinnableRangeSliderProps {
  label?: string;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
  value: number | [number, number];
  onValueChange: (value: number | [number, number]) => void;
  isPinned: boolean;
  onPinnedChange: (pinned: boolean) => void;
  histogram?: number[];
  unit?: string;
  formatValue?: (value: number) => string;
  /** Whether this parameter is disabled due to a dependency (e.g., parent filter is OFF) */
  disabled?: boolean;
  /** Human-readable reason why the parameter is disabled */
  disabledReason?: string;
}

export function PinnableRangeSlider({
  label,
  description,
  min = 0,
  max = 100,
  step = 1,
  value,
  onValueChange,
  isPinned,
  onPinnedChange,
  histogram,
  unit,
  formatValue,
  disabled = false,
  disabledReason,
}: PinnableRangeSliderProps) {
  const [localValue, setLocalValue] = React.useState<[number, number]>(
    Array.isArray(value) ? value : [value, value],
  );

  // Raw text the user is typing into the pinned-value input. `null` means
  // "not editing", so the input shows the committed value. Buffering the
  // in-progress text lets users clear the field and type freely instead of
  // each keystroke being parsed, clamped and snapped back immediately.
  const [pinnedDraft, setPinnedDraft] = React.useState<string | null>(null);

  // Store the original range before pinning so we can restore it
  const originalRangeRef = React.useRef<[number, number] | null>(null);

  // Remember the most recent fixed (pinned) value so re-locking after an
  // unlock returns to it instead of snapping to the range midpoint. Without
  // this, a param that starts pinned (or is edited while pinned) loses its
  // value on lock → unlock → lock, because the midpoint is recomputed from the
  // restored range (AIE-536).
  const lastPinnedValueRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (Array.isArray(value)) {
      setLocalValue(value);
      // Update the original range only when we have an actual range (not pinned)
      if (!isPinned) {
        originalRangeRef.current = value;
      }
    } else {
      setLocalValue([value, value]);
      // Track the committed fixed value (initial pinned value or an edit) so a
      // later re-lock can restore it. Guard on isPinned so we only ever
      // remember a genuinely pinned value — by convention across all consumers
      // `value` is a scalar exactly when pinned and a range when not.
      if (isPinned) {
        lastPinnedValueRef.current = value;
      }
    }
  }, [value, isPinned]);

  const handlePinToggle = () => {
    // Don't allow pin toggle when disabled
    if (disabled) return;

    const newPinned = !isPinned;
    onPinnedChange(newPinned);

    if (newPinned) {
      // Store the current range before pinning
      originalRangeRef.current = localValue;
      // Prefer the previously pinned value so lock → unlock → lock round-trips
      // losslessly. Fall back to the range midpoint only when there is no
      // remembered value or it no longer fits the current range (e.g. the user
      // narrowed the range while unlocked).
      const midpoint = Math.round((localValue[0] + localValue[1]) / 2);
      const remembered = lastPinnedValueRef.current;
      const pinnedValue =
        remembered !== null &&
        remembered >= localValue[0] &&
        remembered <= localValue[1]
          ? remembered
          : midpoint;
      lastPinnedValueRef.current = pinnedValue;
      onValueChange(pinnedValue);
    } else {
      // When unpinning, restore the original range
      if (originalRangeRef.current) {
        onValueChange(originalRangeRef.current);
      } else {
        // No original range (e.g., imported pinned value) — use full slider range
        onValueChange([min, max]);
      }
    }
  };

  const handleRangeChange = (newValue: number[]) => {
    setLocalValue(newValue as [number, number]);
    onValueChange(newValue as [number, number]);
  };

  // While typing, reflect the keystrokes verbatim without parsing or clamping.
  const handlePinnedValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Don't allow value changes when disabled
    if (disabled) return;
    setPinnedDraft(e.target.value);
  };

  // Parse, clamp and commit the typed value when the field loses focus (or the
  // user presses Enter). Empty or non-numeric input is discarded, reverting to
  // the last committed value.
  const commitPinnedValue = () => {
    const raw = pinnedDraft;
    if (raw !== null) setPinnedDraft(null);
    if (disabled || raw === null) return;

    const newValue = parseFloat(raw);
    if (isNaN(newValue)) return;

    const clampedValue = Math.max(min, Math.min(max, newValue));
    onValueChange(clampedValue);
  };

  const handlePinnedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  // Helper to determine decimal places from step value
  const getDecimalPlaces = (stepValue: number): number => {
    const stepStr = stepValue.toString();
    const decimalIndex = stepStr.indexOf(".");
    return decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1;
  };

  const formatDisplay = (val: number) => {
    if (formatValue) return formatValue(val);

    // Format based on step precision
    const decimals = getDecimalPlaces(step);
    const formatted =
      decimals > 0 ? val.toFixed(decimals) : Math.round(val).toString();

    return unit ? `${formatted}${unit}` : formatted;
  };

  return (
    <motion.div
      className="space-y-4"
      animate={{ opacity: disabled ? 0.5 : 1 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {label && (
            <label className="text-sm font-medium text-foreground">
              {label}
            </label>
          )}
          {description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-3.5 h-3.5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="bg-dark-800 text-white p-3 rounded-lg max-w-xs border border-dark-700">
                  <p className="text-sm">{description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Show disabled badge OR value display - not both */}
          {disabled && disabledReason ? (
            <span className="inline-flex items-center gap-1 bg-dark-700/80 text-muted-foreground border border-dark-600 px-2 py-0.5 rounded-full text-xs font-medium">
              <AlertCircle className="w-3 h-3" />
              {disabledReason}
            </span>
          ) : isPinned ? (
            <span className="text-sm font-mono text-muted-foreground">
              {formatDisplay(typeof value === "number" ? value : localValue[0])}
            </span>
          ) : (
            <span className="text-sm font-mono text-muted-foreground">
              {formatDisplay(localValue[0])} - {formatDisplay(localValue[1])}
            </span>
          )}

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handlePinToggle}
                  disabled={disabled}
                  className={cn(
                    "p-1.5 rounded-md transition-all duration-200",
                    "hover:bg-dark-700/50 active:scale-95",
                    "disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:active:scale-100",
                    isPinned
                      ? "text-brand-blue bg-brand-blue/10 ring-1 ring-brand-blue/30"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <motion.div
                    animate={{ rotate: isPinned ? 45 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {isPinned ? (
                      <Lock className="w-4 h-4" />
                    ) : (
                      <Pin className="w-4 h-4" />
                    )}
                  </motion.div>
                </button>
              </TooltipTrigger>
              <TooltipContent className="bg-dark-800 text-white p-2 rounded-lg border border-dark-700">
                <p className="text-sm">
                  {disabled
                    ? disabledReason || "Parameter disabled"
                    : isPinned
                      ? "Unlock parameter (make variable)"
                      : "Pin parameter (fix value)"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isPinned ? (
          <motion.div
            key="pinned"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "rounded-lg border-2 border-brand-blue/20 bg-brand-blue/5",
              "p-4 space-y-2",
            )}
          >
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-brand-blue" />
              <span className="text-sm text-brand-blue font-medium">
                Parameter locked
              </span>
            </div>

            <div className="relative">
              <input
                type="number"
                value={
                  pinnedDraft ??
                  (typeof value === "number" ? value : localValue[0])
                }
                onChange={handlePinnedValueChange}
                onBlur={commitPinnedValue}
                onKeyDown={handlePinnedKeyDown}
                disabled={disabled}
                className={cn(
                  "w-full rounded-lg bg-dark-800/50 border border-dark-700",
                  "pl-3 pr-12 py-2 text-sm text-foreground",
                  // Hide the native number spinner (it clips multi-digit values on focus).
                  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                  "focus:outline-none focus:ring-2 focus:ring-brand-blue/50",
                  "focus:border-brand-blue transition-all duration-200",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
                min={min}
                max={max}
                step={step}
              />
              {unit && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {unit}
                </span>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="unpinned"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <AdvancedRangeSlider
              min={min}
              max={max}
              step={step}
              value={localValue}
              onValueChange={handleRangeChange}
              disabled={disabled}
              {...(histogram && { histogram })}
              {...(unit && { unit })}
              {...(formatValue && { formatValue })}
              showInputs={true}
              showTooltipValues={true}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
